import React, { useState, useCallback } from 'react';
import type { FC } from 'react';
import { Alert, Box, PointerIcon, Text } from '@wix/cli-ui-kit';
import { ErrorViewer, useErrorReporter } from '@wix/cli-error-reporting';
import type { Site } from '@wix/site-list-client';
import { SiteSelector } from '@wix/cli-site-selector';
import { useBiLogger } from '@wix/cli-telemetry-react';
import { useProjectModel } from '../../../model.js';
import { useSite } from './site/index.js';
import { CloseScreen } from '../../CloseScreen/index.js';
import { ScreenState } from './screen-state.js';

interface Props {
  onScreenChange: (screen: ScreenState) => void;
}

export const SiteSelectorScreen: FC<Props> = ({ onScreenChange }) => {
  const { model } = useProjectModel();
  const biLogger = useBiLogger();
  const { siteId, setSiteId } = useSite();
  const { reportError } = useErrorReporter();
  const [error, setError] = useState<unknown>(null);

  const handleError = useCallback(
    (error: unknown) => {
      reportError(error);
      setError(error);
    },
    [reportError, setError]
  );

  const handleSubmit = useCallback(
    async (site: Site) => {
      try {
        await setSiteId(site.id);
        onScreenChange(ScreenState.Home());
      } catch (e) {
        handleError(e);
      }
    },
    [setSiteId, onScreenChange, handleError]
  );

  return (
    <Box flexDirection="column" gap={1}>
      <Box gap={1}>
        <PointerIcon />
        <Text>Current development site: {siteId}</Text>
      </Box>
      <Box flexDirection="column">
        <SiteSelector
          appId={model.config.appId}
          biLogger={biLogger}
          onSubmit={handleSubmit}
          onError={handleError}
        />
        {error ? (
          <ErrorViewer
            error={error}
            systemErrorOverride={
              <Alert type="error">
                Failed to change development site. Try selecting a different
                site or run the dev command again.
              </Alert>
            }
          />
        ) : null}
      </Box>
      <CloseScreen onClose={() => onScreenChange(ScreenState.Home())} />
    </Box>
  );
};
